import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../api/client";

// 0016 Slice 2 + Slice 3 — every admin write hook. Each follows the
// usePatchProductTitle template (commit 1daf42a): typed apiClient
// call, error-envelope decoding, on-success invalidateQueries
// targeting the admin namespace.

type ApiError = {
  error?: { code?: string; message?: string };
} | null | undefined;

function envelopeMessage(err: ApiError): string {
  const code = err?.error?.code ?? "";
  const message = err?.error?.message ?? "Request failed";
  switch (code) {
    case "admin.self_demote_forbidden":
      return "You can't demote yourself.";
    case "admin.user_not_tombstoned":
      return "Cascade-recover only valid for tombstoned users.";
    case "admin.not_found":
      return "User not found.";
    case "auth.banned":
      return "Account suspended.";
    default:
      return message;
  }
}

export function usePromoteUser(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.POST(
        "/api/v1/admin/users/{uid}/promote",
        { params: { path: { uid }, header: { "X-Requested-With": "xhr" } } },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useDemoteUser(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE(
        "/api/v1/admin/users/{uid}/promote",
        { params: { path: { uid }, header: { "X-Requested-With": "xhr" } } },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid] });
      qc.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useGrantCredits(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { amount: number; reason?: string }) => {
      const { error } = await apiClient.POST(
        "/api/v1/admin/users/{uid}/credits",
        {
          params: { path: { uid }, header: { "X-Requested-With": "xhr" } },
          body: { amount: vars.amount, reason: vars.reason },
        },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid] });
    },
  });
}

export function useBanUser(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { reason: string }) => {
      const { error } = await apiClient.POST(
        "/api/v1/admin/users/{uid}/ban",
        {
          params: { path: { uid }, header: { "X-Requested-With": "xhr" } },
          body: { reason: vars.reason },
        },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid] });
    },
  });
}

export function useUnbanUser(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE(
        "/api/v1/admin/users/{uid}/ban",
        { params: { path: { uid }, header: { "X-Requested-With": "xhr" } } },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid] });
    },
  });
}

export function usePatchRateLimit(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { runs?: number; undos?: number }) => {
      const { error } = await apiClient.PATCH(
        "/api/v1/admin/users/{uid}/rate-limit",
        {
          params: { path: { uid }, header: { "X-Requested-With": "xhr" } },
          body: { runs: vars.runs, undos: vars.undos },
        },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid] });
    },
  });
}

export function useCascadeRecover(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ products_deleted: number; runs_deleted: number }> => {
      const { data, error } = await apiClient.POST(
        "/api/v1/admin/users/{uid}/cascade-delete-recover",
        { params: { path: { uid }, header: { "X-Requested-With": "xhr" } } },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
      return (data as { products_deleted: number; runs_deleted: number }) ?? { products_deleted: 0, runs_deleted: 0 };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid] });
    },
  });
}

export function usePatchPlan(planID: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { display_name?: string; grant_amount?: number }) => {
      const { error } = await apiClient.PATCH(
        "/api/v1/admin/plans/{plan_id}",
        {
          params: { path: { plan_id: planID }, header: { "X-Requested-With": "xhr" } },
          body: { display_name: vars.display_name, grant_amount: vars.grant_amount },
        },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "plans"] });
    },
  });
}

export function useImpersonate(uid: string) {
  return useMutation({
    mutationFn: async (vars: { ttl_seconds?: number }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/admin/users/{uid}/impersonate",
        {
          params: { path: { uid }, header: { "X-Requested-With": "xhr" } },
          body: { ttl_seconds: vars.ttl_seconds },
        },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
      return data as { expires_at: string; target_uid: string };
    },
  });
}

export function useDeleteAllSessions(uid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ sessions_terminated: number }> => {
      const { data, error } = await apiClient.DELETE(
        "/api/v1/admin/users/{uid}/sessions",
        { params: { path: { uid }, header: { "X-Requested-With": "xhr" } } },
      );
      if (error) throw new Error(envelopeMessage(error as ApiError));
      return (data as { sessions_terminated: number }) ?? { sessions_terminated: 0 };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "user", uid, "sessions"] });
    },
  });
}
